/**
 * Capabilities — frontend wrapper around `GET /api/auth/permissions`.
 *
 * Backend returns a `{role, permissions: {<capability>: bool, …}}` map for
 * the 11 canonical roles (see `backend/server.py::ROLE_CAPABILITIES`). We
 * fetch it once on auth bootstrap (AuthProvider) and expose:
 *   • `useAuth().permissions` — raw boolean map (or null while loading)
 *   • `useCan("reports")`   — safe hook; `true` iff the cap is backend-allowed
 *
 * Drift-proofing contract:
 *   1. Sidebar items gate on BOTH the role-spec (visibility) AND the backend
 *      capability map — so if a capability is revoked backend-side the menu
 *      item disappears immediately even before we redeploy the sidebar spec.
 *   2. On fetch failure we default to `RESTRICTIVE_FALLBACK` (read_only-like,
 *      never super_admin) so a broken network never escalates permissions.
 *   3. Demo-only preview (multi-role in SidebarDemo) deliberately bypasses
 *      the capability gate — it's a pure visualization tool.
 */
import { useAuth } from "@/App";

/** Read-only-like safe fallback.  Matches `backend::ROLE_CAPABILITIES.read_only`. */
export const RESTRICTIVE_FALLBACK = Object.freeze({
  crm: true, marketing: false, contacts: true, leads: true,
  communications: false, tasks: false, quotes: false,
  calendar_crm: false, calendar_marketing: false,
  demo_bookings: false, dialer: false,
  social_media: false, campaigns: false, email_builder: false,
  resources: false, ai_agents: false, reports: false,
  templates: false,
  settings: true, admin_ops: false, export_data: false,
  edit_scheduled_posts: false, view_all_contacts: false,
  videos_admin: false,
});

/** Sidebar-spec key → backend capability key.  `null` = no gate (always shown
 *  subject to spec, e.g. dashboard/settings are universal to all roles). */
export const SIDEBAR_CAP_KEY = Object.freeze({
  dashboard: null,
  contacts: "contacts",
  leads: "leads",
  comms: "communications",
  quotes: "quotes",
  documents: null,
  demoBookings: "demo_bookings",
  calendar: "calendar_crm",
  dialer: "dialer",
  aiAgents: "ai_agents",
  resources: "resources",
  templates: "templates",
  reports: "reports",
  settings: null,
  tasks: "tasks",
});

/**
 * Resolve the effective capability map for the current session.
 *   • Fetched map (from /auth/permissions) when available.
 *   • `RESTRICTIVE_FALLBACK` while loading OR on fetch error — this is the
 *     "fail closed" behavior required by the defense-in-depth spec.
 */
export function useCapabilities() {
  const { permissions, permsError } = useAuth() || {};
  if (permsError) return RESTRICTIVE_FALLBACK;
  if (!permissions) return RESTRICTIVE_FALLBACK;
  return permissions;
}

/**
 * `useCan("reports")` → boolean. Safe: unknown capability keys return
 * `false` (never leak access by typo).
 */
export function useCan(cap) {
  const caps = useCapabilities();
  if (!cap) return true; // null/undefined = ungated
  return caps[cap] === true;
}

/** Non-hook helper for plain-JS callers (e.g. sidebar section filtering). */
export function canFromMap(caps, cap) {
  if (!cap) return true;
  if (!caps) return false;
  return caps[cap] === true;
}
